import React from 'react';
import { Checkbox, FormGroup } from '@material-ui/core';
import ReactPlaceholder from 'react-placeholder/lib/ReactPlaceholder';
import classNames from 'clsx';

import { DictionaryEntry } from 'app/services/dict.service';
import { FormTooltip } from 'app/components/FormTooltip';
import { ControlContainer } from 'app/components/ControlContainer';
import { TravelPurposeIcon } from 'app/components/TravelPurposeIcon';
import { travelGoalsDescriptionsDictionary } from 'app/shared/dictionaries/travelGoalsDescriptionsDictionary';
import { IconCheckboxGroupControlProps } from './IconCheckboxGroupControl.interfaces';
import { InputPlaceholder } from 'app/components/InputPlaceholder';
import { useCheckboxGroupControl } from 'app/shared/hooks/controls/useCheckboxGroupControl';
import { useStyles } from './IconCheckboxGroupControl.styles';

export default function IconCheckboxGroupControl(
  props: IconCheckboxGroupControlProps
) {
  const classes = useStyles();

  const { helperText, input, isLoading, meta, tooltip, width } = props;
  const error = meta.touched && !meta.valid;

  const {
    handleChange,
    handleClick,
    isChecked,
    renderCheckboxes,
  } = useCheckboxGroupControl(props);

  const renderEntryTooltip = (entryKey: DictionaryEntry['key']) => {
    const dictionaryEntry = travelGoalsDescriptionsDictionary.find(
      (entry: DictionaryEntry) => entry.key === entryKey
    );

    return dictionaryEntry?.value;
  };

  return (
    <ReactPlaceholder
      ready={!isLoading}
      customPlaceholder={<InputPlaceholder width={width} />}
    >
      <ControlContainer
        inputId={input.name}
        tooltip={tooltip}
        width={width}
        helperText={helperText}
        meta={meta}
        filled={input.value && input.value.length}
      >
        <FormGroup
          row
          className={classNames(classes.formGroup, { error: error })}
        >
          {renderCheckboxes(renderCheckbox)}
        </FormGroup>
      </ControlContainer>
    </ReactPlaceholder>
  );

  function renderCheckbox(entry: DictionaryEntry) {
    return (
      <div
        onClick={handleClick}
        key={entry.key}
        className={classes.checkboxRow}
      >
        <label
          className={classNames(classes.checkboxLabel, 'focusVisibleOutline')}
          tabIndex={0}
          onKeyDown={(e) => {
            if (
              e.target === e.currentTarget &&
              (e.key === 'Enter' || e.key === ' ')
            ) {
              e.preventDefault();
              handleChange(entry.key);
            }
          }}
        >
          <Checkbox
            name={input.name}
            color="primary"
            className={classes.checkbox}
            checked={isChecked(entry.key)}
            onChange={() => handleChange(entry.key)}
            value={entry.key}
            tabIndex={-1}
          />
          <TravelPurposeIcon dictionaryEntryKey={entry.key} />
          <span className={classes.label}>{entry.value}</span>
        </label>
        <FormTooltip
          content={renderEntryTooltip(entry.key)}
          className={classes.helpIcon}
        />
      </div>
    );
  }
}
